import { useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { TextField, Button, Checkbox, IconButton, TableContainer, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useEffect, useState } from "react";
import { showError, showToast } from "../../constants/toasts";
import { useAddRoleMutation, useLazyRoleByIdQuery } from "../../service/subAdmin";
import Loader from "../../constants/Loader";

interface Permission {
  moduleName: string;
  view: boolean;
  add: boolean;
  edit: boolean;
  delete: boolean;
}

const AddRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [addRole] = useAddRoleMutation()
  const [getRoleById] = useLazyRoleByIdQuery()


  const rows = [
    {
      id: 1,
      name: "Dashboard",
    },
    {
      id: 2,
      name: "Booking & RSVP Tracker",
    },
    {
      id: 3,
      name: "Cancellation Requests",

    },
    {
      id: 4,
      name: "Offer Management",

    },
    {
      id: 5,
      name: "Category",

    },
    {
      id: 6,
      name: "Group & Tags",

    },
    {
      id: 7,
      name: "Butterfly Effect",
    },
    {
      id: 8,
      name: "Members",
    },
    {
      id: 9,
      name: "Groups",
    },
    {
      id: 10,
      name: "Add Employee",
    },
    {
      id: 11,
      name: "Manage Subscriptions",
    },
    {
      id: 12,
      name: "Transactions",
    },
    {
      id: 13,
      name: "Reports",
    },
    {
      id: 14,
      name: "Notification & Email",
    },
    {
      id: 15,
      name: "Manage CMS Pages",
    },

  ];

  const [permissions, setPermissions] = useState<Permission[]>(
    rows?.map((row) => ({
      moduleName: row?.name,
      view: row?.name === "Dashboard" ? true : false, // Dashboard view true by default
      add: false,
      edit: false,
      delete: false,
    }))
  );


  const [roleName, setRoleName] = useState<string>("");
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleCheckboxChange = (index: number, field: keyof Permission) => {
    setPermissions((prev) =>
      prev.map((perm, i) => {
        if (i === index) {
          let updatedPerm = { ...perm, [field]: !perm[field] };

          // If add/edit/delete is checked, ensure view is also checked
          if ((field === "add" || field === "edit" || field === "delete") && updatedPerm[field]) {
            updatedPerm.view = true;
          }

          // If view is unchecked, uncheck add, edit, delete
          if (field === "view" && !updatedPerm.view) {
            updatedPerm.add = false;
            updatedPerm.edit = false;
            updatedPerm.delete = false;
          }

          return updatedPerm;
        }
        return perm;
      })
    );
  };



  const addRoleApi = async () => {
    setError(true);
    if (roleName?.trim() === "") {
      showError("Role name is required");
      return;
    }


    try {
      setLoading(true);
      const body = {
        roleName,
        permissions
      }

      let response = await addRole(body).unwrap();

      if (response?.status === 200) {
        setError(false);
        showToast(response?.message || '')
        goBack()
      } else {
        showError(response?.message || '')
      }
    } catch (error: any) {
      setLoading(false);
      showError(error?.data?.message || "");
    }
  };

  const getRoleByIdApi = async () => {
    try {
      setLoading(true)
      const response = await getRoleById({ id }).unwrap();
      setLoading(false)
      if (response.status === 200) {
        const perms = response?.data?.permissions || [];

        // Ensure Dashboard always has view: true
        const updatedPerms = rows.map((row) => {
          const existingPerm = perms?.find((p: any) => p?.moduleName === row?.name);
          if (row?.name === "Dashboard") {
            return {
              moduleName: "Dashboard",
              view: true,
              add: false,
              edit: false,
              delete: false
            }
          }
          return existingPerm || {
            moduleName: row.name,
            view: false,
            add: false,
            edit: false,
            delete: false
          }
        });

        setRoleName(response?.data?.roleName || "")
        setPermissions(updatedPerms)
      }
    } catch (error: any) {
      setLoading(false)
      showError(error?.data?.message || "");
    }
  };



  const goBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (id) {
      getRoleByIdApi()
    }
  }, [id])

  return (
    <MainContainer>
      <Loader isLoad={loading} />
      <div className="main_title">
        <h1 className="back_arrow">
          {" "}
          <IconButton onClick={goBack}>
            {" "}
            <ArrowBackIcon />{" "}
          </IconButton>
          Add Role
        </h1>
      </div>

      <div className="cards table_cards v2">
        <div className="form">
          <div className="p_20">
            <div className="gap_p">
              <div className="control_group w_50">
                <label htmlFor="roleName">Role Name</label>
                <TextField
                  id="roleName"
                  aria-labelledby="roleNameLabel"
                  placeholder="Enter Role Name"
                  fullWidth
                  hiddenLabel
                  value={roleName}

                  onChange={(e) => {
                    if (e.target.value === " " || e.target.value === ".") {
                    } else {
                      setRoleName(e.target.value);
                    }
                  }}
                  inputProps={{
                    maxLength: 40,
                    'aria-label': 'Role Name',
                  }}
                />
              </div>
            </div>
          </div>
          <TableContainer sx={{ mt: 4 }} className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Module Name</TableCell>
                  <TableCell align="center">View</TableCell>
                  <TableCell align="center">Add</TableCell>
                  <TableCell align="center">Edit</TableCell>
                  <TableCell align="center">Delete</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {permissions?.map((perm, index) => (
                  <TableRow key={index}>
                    <TableCell>{perm?.moduleName}</TableCell>
                    <TableCell align="center">
                      <Checkbox
                        checked={perm?.view}
                        onChange={() => handleCheckboxChange(index, "view")}
                        disabled={perm?.moduleName === "Dashboard"}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {
                        perm.moduleName === "Dashboard" ? "-" : <Checkbox
                          checked={perm?.add}
                          onChange={() => handleCheckboxChange(index, "add")}
                        />
                      }

                    </TableCell>
                    <TableCell align="center">
                      {
                        perm.moduleName === "Dashboard" ? "-" : <Checkbox
                          checked={perm?.edit}
                          onChange={() => handleCheckboxChange(index, "edit")}
                        />
                      }

                    </TableCell>
                    <TableCell align="center">
                      {
                        perm.moduleName === "Dashboard" ? "-" : <Checkbox
                          checked={perm?.delete}
                          onChange={() => handleCheckboxChange(index, "delete")}
                        />
                      }

                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

          </TableContainer>
          <div className="form_btn p_20">
            <Button className="btn btn_primary" onClick={addRoleApi}>Save</Button>
          </div>
        </div>
      </div>
    </MainContainer>
  );
};

export default AddRole;
